import { ConnectedUser } from '../../stores/types/connection';
import { Broadcast } from '../../stores/types/broadcasting';
import { OpentokSessionValue, RoomSessionValue } from '../../connection/health';

export enum ProbeStatus {
	/** Service answered and there is something to report. */
	Ok = 'ok',
	/** Service answered, but there is nothing here (no broadcast, no session yet). */
	Empty = 'empty',
	/** Service answered slowly, or answered with a partially failed payload. */
	Degraded = 'degraded',
	/** Service did not answer, or answered with an error status. */
	Error = 'error',
}

/** Latency above this is reported as degraded rather than ok. */
export const DEGRADED_LATENCY_MS = 2000;

export interface Probe<T> {
	status: ProbeStatus;
	message?: string;
	httpStatus: number;
	latencyMs: number;
	data: T | null;
}

export interface RoomHealth {
	fetchedAt: number;
	roomSession: Probe<RoomSessionValue>;
	opentok: Probe<OpentokSessionValue>;
	broadcast: Probe<Broadcast>;
	connectedUsers: Probe<ConnectedUser[]>;
}

export type ProbeKey = Exclude<keyof RoomHealth, 'fetchedAt'>;

export const PROBE_KEYS: ProbeKey[] = [
	'roomSession',
	'opentok',
	'broadcast',
	'connectedUsers',
];

/**
 * Worst-first rollup, so a collapsed section still shows a failure inside it.
 * Absent probes are skipped rather than counted as idle - a section whose data
 * has not arrived yet is not healthy, it is unknown.
 */
export function worstStatus(statuses: (ProbeStatus | undefined)[]): ProbeStatus {
	const present = statuses.filter((status): status is ProbeStatus => !!status);
	if (present.includes(ProbeStatus.Error)) return ProbeStatus.Error;
	if (present.includes(ProbeStatus.Degraded)) return ProbeStatus.Degraded;
	if (present.includes(ProbeStatus.Ok)) return ProbeStatus.Ok;
	return ProbeStatus.Empty;
}

/**
 * Poll cadences offered in the dashboard header. 0 pauses polling.
 *
 * 5s is the floor: every tick fans out to the whole probe set, and faster than
 * this the answers stop being newer than the round trip that fetched them.
 */
export const POLL_INTERVALS_MS = [5000, 15000, 0] as const;

export const DEFAULT_POLL_INTERVAL_MS = 5000;
